/**
 * PhotoCropper (ADR-26; docs/04 §3) — pick a photo, bound it, choose the 16:9
 * region, upload.
 *
 * Why the client resizes at all: a phone photo is 6–12 MB, and TKC's people are
 * often on a site connection when they take it. Downscaling to 2048px before
 * upload turns that into a few hundred KB. The server still re-encodes and is
 * still the only thing that validates (docs/01 §9) — this is a courtesy to the
 * network, never a substitute for the check.
 *
 * Why a hand-written cropper rather than a library: it needs to be operable by
 * keyboard to satisfy docs/03 §8, and the interaction is genuinely small — a
 * scale, an offset, and a fixed 16:9 frame. Arrow keys nudge, +/- zoom, and the
 * whole surface is one focusable control with an accessible description.
 *
 * The crop is reported in SOURCE pixels (the downscaled image the server will
 * receive), because that is the coordinate space the server crops in.
 */

import { clsx } from 'clsx';
import { ImageUp, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { Button } from './Button.js';

/** Matches SOURCE_MAX_EDGE in the server pipeline. */
const MAX_EDGE = 2048;
const ASPECT = 16 / 9;
/**
 * The narrowest rectangle the server will accept — its thumb width, and so the
 * smallest crop that can be rendered without upscaling. The zoom ceiling is
 * derived from it rather than fixed, because a fixed ceiling makes some VALID
 * stored crops unrepresentable: a 160-wide rectangle in a 2048-wide source
 * needs 12.8x, and at the old ceiling of 4x reopening it silently showed a
 * different framing than the one that was saved (RM-029).
 */
const MIN_CROP_WIDTH = 160;
/**
 * Zoom steps MULTIPLY. The ceiling now depends on the source and can reach
 * ~12.8x, which additive quarter-steps would take five hundred presses to
 * cross. A ratio keeps the first press worth the same as it always was (1 ->
 * 1.25) and the whole range about twenty presses wide.
 */
const ZOOM_STEP = 1.25;

/** The largest 16:9 box that fits in an image — the crop at zoom 1. */
function baseBox(width: number, height: number): { width: number; height: number } {
  const fitByWidth = width / ASPECT <= height;
  return fitByWidth ? { width, height: width / ASPECT } : { width: height * ASPECT, height };
}

/** How far this image may be zoomed before the crop would be smaller than the server allows. */
export function zoomCeiling(image: { width: number; height: number }): number {
  return Math.max(1, baseBox(image.width, image.height).width / MIN_CROP_WIDTH);
}

export interface SavedFraming {
  readonly crop: { x: number; y: number; width: number; height: number };
  readonly sourceWidth: number;
  readonly sourceHeight: number;
}

/**
 * WHERE THE CROPPER OPENS (RM-029) — pure, so it can be asserted directly.
 *
 * With a replayable framing it opens on exactly that rectangle: the zoom is how
 * much smaller the rectangle is than the whole 16:9 box, and the offset is the
 * rectangle's own corner. Everything else opens centred — a legacy photo, whose
 * rectangle was measured against an original that no longer exists (docs/11
 * F-55), and a source that did not load at the size the rectangle was measured
 * against, where the coordinates would mean something other than what they say.
 */
export function openingFrame(
  image: { width: number; height: number },
  framing?: SavedFraming,
): { zoom: number; offset: { x: number; y: number } } {
  const base = baseBox(image.width, image.height);
  const centred = {
    zoom: 1,
    offset: { x: (image.width - base.width) / 2, y: (image.height - base.height) / 2 },
  };
  if (framing === undefined) return centred;
  if (framing.sourceWidth !== image.width || framing.sourceHeight !== image.height) return centred;
  if (framing.crop.width <= 0) return centred;
  return {
    zoom: Math.min(zoomCeiling(image), Math.max(1, base.width / framing.crop.width)),
    offset: { x: framing.crop.x, y: framing.crop.y },
  };
}

export interface CropResult {
  /** JPEG bytes to upload — already oriented and bounded. */
  readonly file: Blob;
  /** Crop rectangle in the uploaded image's pixels. */
  readonly crop: { x: number; y: number; width: number; height: number };
}

export interface PhotoCropperProps {
  /** Called with the bytes and rectangle when the user confirms. */
  readonly onConfirm: (result: CropResult) => void;
  readonly onCancel: () => void;
  readonly busy?: boolean | undefined;
  /**
   * ADJUST-CROP MODE (punch 4.3). Given the stored source's URL, the cropper
   * opens on THAT image with no file picker: there is nothing to upload,
   * because the photo is already stored and only its framing is changing.
   *
   */
  readonly sourceUrl?: string | undefined;
  /**
   * The framing to REOPEN, when there is one that can be replayed (RM-029).
   *
   * Passed only for a photo whose stored rectangle is in the current crop
   * space; a legacy photo's rectangle was measured against an original that no
   * longer exists and cannot be replayed (docs/11 F-55), so it arrives
   * undefined and the cropper opens centred. Carrying the source dimensions
   * alongside the rectangle is what makes that decision checkable here: the
   * coordinates mean nothing unless the image that just loaded is the image
   * they were measured against, so they are used only when the dimensions
   * agree, and the centred default stands in otherwise.
   */
  readonly initialFraming?: SavedFraming | undefined;
  /** The action word on the confirm button. */
  readonly confirmLabel?: string | undefined;
}

interface Loaded {
  readonly bitmap: ImageBitmap;
  readonly blob: Blob;
  readonly width: number;
  readonly height: number;
}

/**
 * Decode, bound to MAX_EDGE, and re-encode as JPEG. Uses createImageBitmap with
 * `imageOrientation: 'from-image'` so a phone photo's EXIF rotation is applied
 * here too — otherwise the region the user picks is not the region they see.
 */
async function loadBounded(file: File): Promise<Loaded> {
  const source = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const scale = Math.min(1, MAX_EDGE / Math.max(source.width, source.height));
  const width = Math.round(source.width * scale);
  const height = Math.round(source.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('canvas unavailable');
  context.drawImage(source, 0, 0, width, height);
  source.close();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.9);
  });
  if (blob === null) throw new Error('could not encode the image');
  const bitmap = await createImageBitmap(blob);
  return { bitmap, blob, width, height };
}

export function PhotoCropper({
  onConfirm,
  onCancel,
  busy = false,
  sourceUrl,
  initialFraming,
  confirmLabel = 'Save photo',
}: PhotoCropperProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  // Offset of the crop window's top-left, in source pixels.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragging = useRef<{ x: number; y: number } | null>(null);

  /**
   * The crop rectangle for the current zoom: the largest 16:9 box that fits at
   * zoom 1, divided by the zoom. Deriving it rather than storing it means the
   * rectangle can never drift out of step with the offset.
   */
  const cropSize = useCallback(
    (image: Loaded): { width: number; height: number } => {
      const base = baseBox(image.width, image.height);
      return { width: base.width / zoom, height: base.height / zoom };
    },
    [zoom],
  );

  /** Keeps the crop window inside the image, whatever the zoom or the nudge. */
  const clamp = useCallback(
    (image: Loaded, next: { x: number; y: number }): { x: number; y: number } => {
      const { width, height } = cropSize(image);
      return {
        x: Math.max(0, Math.min(next.x, image.width - width)),
        y: Math.max(0, Math.min(next.y, image.height - height)),
      };
    },
    [cropSize],
  );

  // Re-centre and redraw whenever the image or the zoom changes.
  useEffect(() => {
    if (loaded === null) return;
    setOffset((current) => clamp(loaded, current));
  }, [loaded, zoom, clamp]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || loaded === null) return;
    const context = canvas.getContext('2d');
    if (context === null) return;
    const { width, height } = cropSize(loaded);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(
      loaded.bitmap,
      offset.x,
      offset.y,
      width,
      height,
      0,
      0,
      canvas.width,
      canvas.height,
    );
  }, [loaded, offset, cropSize]);

  const pick = async (file: File): Promise<void> => {
    setError(null);
    try {
      const next = await loadBounded(file);
      const opening = openingFrame(next);
      setZoom(opening.zoom);
      setOffset(opening.offset);
      setLoaded(next);
    } catch {
      // The browser could not decode it. The server would refuse it too, but
      // saying so now saves an upload and gives a clearer reason.
      setError('That file could not be read as an image. Try a JPEG, PNG, or WebP.');
    }
  };

  // Adjust-crop mode: fetch the stored source once and open on it. The source
  // route answers `no-store`, so this is a real request every time — which is
  // the point: the uncropped image is editor-only and must not sit in a cache.
  useEffect(() => {
    if (sourceUrl === undefined) return undefined;
    const live = { current: true };
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch(sourceUrl, {
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('source unavailable');
        const blob = await response.blob();
        const next = await loadBounded(new File([blob], 'source.jpg', { type: 'image/jpeg' }));
        if (!live.current) {
          next.bitmap.close();
          return;
        }
        // REOPEN WHERE THE LAST EDIT LEFT OFF (RM-029). The saved rectangle is
        // in this image's own pixels, so zoom is how much smaller it is than
        // the whole 16:9 box and the offset is the rectangle's own corner.
        // Centring is the fallback for a photo whose rectangle cannot be
        // replayed — a legacy one, or a source that did not load at the size
        // the rectangle was measured against — not the standing behaviour.
        const opening = openingFrame(next, initialFraming);
        setZoom(opening.zoom);
        setOffset(opening.offset);
        setLoaded(next);
      } catch {
        if (live.current) setError('The stored image could not be loaded.');
      }
    })();
    return () => {
      live.current = false;
      controller.abort();
    };
  }, [sourceUrl, initialFraming]);

  const nudge = (dx: number, dy: number): void => {
    if (loaded === null) return;
    const step = Math.max(8, Math.round(loaded.width / 50));
    setOffset((current) => clamp(loaded, { x: current.x + dx * step, y: current.y + dy * step }));
  };

  if (loaded === null && sourceUrl !== undefined) {
    return (
      <p className="p-6 text-center text-body text-ink2" role="status">
        {error ?? 'Loading the stored image…'}
      </p>
    );
  }

  if (loaded === null) {
    return (
      <div className="flex flex-col gap-3">
        <label className="flex min-h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-card border border-dashed border-control-line bg-canvas p-6 text-center">
          <ImageUp size={24} aria-hidden className="text-ink2" />
          <span className="text-body font-medium text-ink">Choose a photo</span>
          <span className="text-meta text-ink2">
            JPEG, PNG, or WebP. Large photos are resized before upload.
          </span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="keeper-ignore sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file !== undefined) void pick(file);
            }}
          />
        </label>
        {error !== null ? (
          <p role="alert" className="text-meta font-medium text-red-fg">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  const { width: cropWidth, height: cropHeight } = cropSize(loaded);
  // Derived from THIS image, so the smallest crop the server will take is
  // always reachable and nothing larger than it ever is.
  const maxZoom = zoomCeiling(loaded);

  return (
    <div className="flex flex-col gap-3">
      {/*
        One focusable control rather than a canvas plus separate buttons for
        every direction: a screen-reader user gets the description below, and a
        keyboard user gets arrows and +/- without tabbing through eight targets.
      */}
      <canvas
        ref={canvasRef}
        width={640}
        height={360}
        role="application"
        tabIndex={0}
        aria-label="Crop the photo"
        aria-describedby="cropper-help"
        className={clsx(
          'aspect-video w-full cursor-grab touch-none rounded-card border border-card-line bg-photo-grad-a',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700',
        )}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          dragging.current = { x: event.clientX, y: event.clientY };
        }}
        onPointerMove={(event) => {
          const start = dragging.current;
          if (start === null) return;
          const rect = event.currentTarget.getBoundingClientRect();
          // Pointer movement is in CSS pixels; the offset is in source pixels,
          // so it scales by how much of the image the frame is showing.
          const ratio = cropWidth / rect.width;
          setOffset((current) =>
            clamp(loaded, {
              x: current.x - (event.clientX - start.x) * ratio,
              y: current.y - (event.clientY - start.y) * ratio,
            }),
          );
          dragging.current = { x: event.clientX, y: event.clientY };
        }}
        onPointerUp={() => {
          dragging.current = null;
        }}
        onKeyDown={(event) => {
          const moves: Record<string, [number, number]> = {
            ArrowLeft: [-1, 0],
            ArrowRight: [1, 0],
            ArrowUp: [0, -1],
            ArrowDown: [0, 1],
          };
          const move = moves[event.key];
          if (move !== undefined) {
            event.preventDefault();
            nudge(move[0], move[1]);
            return;
          }
          if (event.key === '+' || event.key === '=') {
            event.preventDefault();
            setZoom((z) => Math.min(maxZoom, z * ZOOM_STEP));
          }
          if (event.key === '-' || event.key === '_') {
            event.preventDefault();
            setZoom((z) => Math.max(1, z / ZOOM_STEP));
          }
        }}
      />
      <p id="cropper-help" className="text-meta text-ink2">
        Drag to move the photo. Arrow keys nudge it; <kbd>+</kbd> and <kbd>−</kbd> zoom. The frame
        is the shape it will appear in.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1">
          <Button
            variant="ghost"
            aria-label="Zoom out"
            disabledReason={zoom <= 1 ? 'Already showing the whole photo' : undefined}
            onClick={() => {
              setZoom((z) => Math.max(1, z / ZOOM_STEP));
            }}
          >
            <ZoomOut size={16} aria-hidden />
          </Button>
          <Button
            variant="ghost"
            aria-label="Zoom in"
            disabledReason={zoom >= maxZoom ? 'Maximum zoom' : undefined}
            onClick={() => {
              setZoom((z) => Math.min(maxZoom, z * ZOOM_STEP));
            }}
          >
            <ZoomIn size={16} aria-hidden />
          </Button>
          {sourceUrl === undefined ? (
            <Button
              variant="ghost"
              aria-label="Choose a different photo"
              onClick={() => {
                loaded.bitmap.close();
                setLoaded(null);
              }}
            >
              <RotateCcw size={16} aria-hidden />
            </Button>
          ) : null}
        </span>
        <span className="flex gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            onClick={() => {
              onConfirm({
                file: loaded.blob,
                crop: {
                  x: Math.round(offset.x),
                  y: Math.round(offset.y),
                  width: Math.round(cropWidth),
                  height: Math.round(cropHeight),
                },
              });
            }}
          >
            {confirmLabel}
          </Button>
        </span>
      </div>
    </div>
  );
}
